import { exportStyledExcel } from '../../../../utils/methods';
import {
  STATUS_LABEL,
  TYPE_LABEL,
  TICKET_TYPE_LABEL,
  classNameOf,
  entryLabelOf,
} from './attendanceShared';

/**
 * 출결 탭 엑셀 내려받기. 지금 보고 있는 뷰(내역/출석부/회원별/방문)와 걸어 둔 조건을
 * 그대로 옮긴다.
 *
 * <p>호출부에서 떼어낸 이유는 뷰마다 컬럼 정의가 달라 130줄을 차지했고, 그 덩어리가
 * 화면 로직 한가운데에 있어 출결 탭 본체가 읽히지 않았기 때문이다. 동작은 그대로다.
 * 실패는 던지고, 사용자 안내(토스트)는 호출부가 맡는다.
 */
export const downloadAttendanceExcel = async ({
  view,
  classStats,
  usageStats,
  visibleVisits,
  book,
  memberStats,
  visibleRows,
  group,
  startDate,
  endDate,
}) => {
  if (view === 'visits') {
    await exportStyledExcel(
      visibleVisits.map(visit => ({
        일자: visit.visitDate,
        회원명: visit.memberName || '-',
        구분: TICKET_TYPE_LABEL[visit.ticketType] || '-',
        '입장 상품': visit.ticketName || '-',
        입실: visit.entryTime || '-',
        퇴실: visit.exitTime ? `${visit.exitNextDay ? '익일 ' : ''}${visit.exitTime}` : '-',
        '이용 시간(분)': visit.stayMinutes == null ? '-' : visit.stayMinutes,
        '입장 방식': entryLabelOf(visit),
        '수업 연결': visit.linkedToReservation ? 'Y' : 'N',
      })),
      {
        filename: `그룹방문_${group.name}_${startDate}_${endDate}`,
        sheetName: '방문 내역',
        columnAlignments: {
          일자: 'center',
          회원명: 'left',
          구분: 'center',
          '입장 상품': 'left',
          입실: 'center',
          퇴실: 'center',
          '이용 시간(분)': 'center',
          '입장 방식': 'center',
          '수업 연결': 'center',
        },
      },
    );
    return;
  }
  if (view === 'book' && book) {
    // 화면의 출석부를 그대로 옮긴다. 열 이름은 머리글과 같게 둔다.
    const columnNames = book.columns.map(column => `${column.label}${column.sub ? ` ${column.sub}` : ''}`);
    await exportStyledExcel(
      book.members.map(member => {
        const row = { 회원명: member.name };
        book.columns.forEach((column, index) => {
          const cell = member.cells.get(column.key);
          if (!cell) {
            row[columnNames[index]] = '';
            return;
          }
          if (book.unit === 'month' || book.unit === 'weekly') {
            row[columnNames[index]] = cell.visited;
            return;
          }
          row[columnNames[index]] = cell.visited > 0 ? '방문' : cell.absent > 0 ? '결석' : '예정';
        });
        row['방문'] = member.visited;
        row['결석'] = member.absent;
        return row;
      }),
      {
        filename: `그룹출석부_${group.name}_${startDate}_${endDate}`,
        sheetName: '출석부',
        columnAlignments: {
          회원명: 'left',
          방문: 'center',
          결석: 'center',
          ...Object.fromEntries(columnNames.map(name => [name, 'center'])),
        },
      },
    );
    return;
  }
  if (view === 'usage') {
    await exportStyledExcel(
      usageStats.map(stat => ({
        회원명: stat.name,
        '방문 일수': stat.visitDays,
        '주당 방문': Number(stat.perWeek.toFixed(1)),
        '총 이용 시간(분)': stat.stayCount ? stat.stayTotal : '-',
        '평균 이용 시간(분)': stat.avgStay == null ? '-' : stat.avgStay,
        '주 이용 상품': stat.topTicket || '-',
        '마지막 방문': stat.lastVisit || '-',
      })),
      {
        filename: `그룹이용통계_${group.name}_${startDate}_${endDate}`,
        sheetName: '이용 통계',
        columnAlignments: {
          회원명: 'left',
          '방문 일수': 'center',
          '주당 방문': 'center',
          '총 이용 시간(분)': 'center',
          '평균 이용 시간(분)': 'center',
          '주 이용 상품': 'left',
          '마지막 방문': 'center',
        },
      },
    );
    return;
  }
  if (view === 'classes') {
    await exportStyledExcel(
      classStats.map(stat => ({
        수강반: stat.label,
        유형: TYPE_LABEL[stat.classType] || '-',
        출석: stat.attended,
        결석: stat.absent,
        지각: stat.lateCount,
        예정: stat.reserved,
        '출석률(%)': stat.rate == null ? '-' : stat.rate,
        인원: stat.memberCount,
      })),
      {
        filename: `그룹출결_수강반_${group.name}_${startDate}_${endDate}`,
        sheetName: '수강반별 출결',
        columnAlignments: {
          수강반: 'left',
          유형: 'center',
          출석: 'center',
          결석: 'center',
          지각: 'center',
          예정: 'center',
          '출석률(%)': 'center',
          인원: 'center',
        },
      },
    );
    return;
  }
  if (view === 'members') {
    await exportStyledExcel(
      memberStats.map(stat => ({
        회원명: stat.name,
        출석: stat.onTime,
        지각: stat.late,
        결석: stat.absent,
        예정: stat.reserved,
        '지각 누적(분)': stat.lateTotalMinutes || '-',
        '출석률(%)': stat.rate == null ? '-' : stat.rate,
      })),
      {
        filename: `그룹출결_회원별_${group.name}_${startDate}_${endDate}`,
        sheetName: '회원별 출결',
        columnAlignments: {
          회원명: 'left',
          출석: 'center',
          지각: 'center',
          결석: 'center',
          예정: 'center',
          '지각 누적(분)': 'center',
          '출석률(%)': 'center',
        },
      },
    );
    return;
  }
  await exportStyledExcel(
    visibleRows.map(row => ({
      일자: row.classDate,
      시간: `${row.startTime} ~ ${row.endTime}`,
      유형: TYPE_LABEL[row.classType] || row.classType || '-',
      수업명: classNameOf(row),
      회원명: row.memberName || '-',
      전화번호: row.phoneNumber || '-',
      '담당 강사': row.trainerName || '-',
      상태: STATUS_LABEL[row.status] || row.status,
      '입장 방식': row.status === 'ATTENDED' ? entryLabelOf(row) : '-',
      '입장 상품': row.status === 'ATTENDED' ? row.entryTicketName || '-' : '-',
      입실: row.entryTime || '-',
      퇴실: row.exitTime ? `${row.exitNextDay ? '익일 ' : ''}${row.exitTime}` : '-',
    })),
    {
      filename: `그룹출결_${group.name}_${startDate}_${endDate}`,
      sheetName: '출결',
      columnAlignments: {
        일자: 'center',
        시간: 'center',
        유형: 'center',
        수업명: 'left',
        회원명: 'left',
        전화번호: 'left',
        '담당 강사': 'left',
        상태: 'center',
        '입장 방식': 'center',
        '입장 상품': 'left',
        입실: 'center',
        퇴실: 'center',
      },
    },
  );
};
